import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainButton } from '@telegram-apps/sdk-react';
import { TFunction } from 'i18next';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { ZodiacSign } from '@/enums/apiEnums.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import AriesShape from '@/components/common/svg/shapes/zodiac/AriesShape.tsx';
import TaurusShape from '@/components/common/svg/shapes/zodiac/TaurusShape.tsx';
import GeminiShape from '@/components/common/svg/shapes/zodiac/GeminiShape.tsx';
import CancerShape from '@/components/common/svg/shapes/zodiac/CancerShape.tsx';
import LeoShape from '@/components/common/svg/shapes/zodiac/LeoShape.tsx';
import VirgoShape from '@/components/common/svg/shapes/zodiac/VirgoShape.tsx';
import LibraShape from '@/components/common/svg/shapes/zodiac/LibraShape.tsx';
import ScorpioShape from '@/components/common/svg/shapes/zodiac/ScorpioShape.tsx';
import SagittariusShape from '@/components/common/svg/shapes/zodiac/SagittariusShape.tsx';
import CapricornShape from '@/components/common/svg/shapes/zodiac/CapricornShape.tsx';
import AquariusShape from '@/components/common/svg/shapes/zodiac/AquariusShape.tsx';
import PiscesShape from '@/components/common/svg/shapes/zodiac/PiscesShape.tsx';
import ZodiacShape from '@/components/common/svg/shapes/ZodiacShape.tsx';

export const getZodiacSigns = (t: TFunction): Record<ZodiacSign, string> => ({
  [ZodiacSign.ZODIAC_SIGN_UNSPECIFIED]: t('status_unspecified'),
  [ZodiacSign.ZODIAC_SIGN_ARIES]: t('zodiac.aries'),
  [ZodiacSign.ZODIAC_SIGN_TAURUS]: t('zodiac.taurus'),
  [ZodiacSign.ZODIAC_SIGN_GEMINI]: t('zodiac.gemini'),
  [ZodiacSign.ZODIAC_SIGN_CANCER]: t('zodiac.cancer'),
  [ZodiacSign.ZODIAC_SIGN_LEO]: t('zodiac.leo'),
  [ZodiacSign.ZODIAC_SIGN_VIRGO]: t('zodiac.virgo'),
  [ZodiacSign.ZODIAC_SIGN_LIBRA]: t('zodiac.libra'),
  [ZodiacSign.ZODIAC_SIGN_SCORPIO]: t('zodiac.scorpio'),
  [ZodiacSign.ZODIAC_SIGN_SAGITTARIUS]: t('zodiac.sagittarius'),
  [ZodiacSign.ZODIAC_SIGN_CAPRICORN]: t('zodiac.capricorn'),
  [ZodiacSign.ZODIAC_SIGN_AQUARIUS]: t('zodiac.aquarius'),
  [ZodiacSign.ZODIAC_SIGN_PISCES]: t('zodiac.pisces')
});

export const zodiacIcons: Partial<
  Record<
    ZodiacSign,
    FC<{
      width: number;
      height: number;
      fillColor?: string;
      style?: CSSProperties;
    }>
  >
> = {
  [ZodiacSign.ZODIAC_SIGN_ARIES]: AriesShape,
  [ZodiacSign.ZODIAC_SIGN_TAURUS]: TaurusShape,
  [ZodiacSign.ZODIAC_SIGN_GEMINI]: GeminiShape,
  [ZodiacSign.ZODIAC_SIGN_CANCER]: CancerShape,
  [ZodiacSign.ZODIAC_SIGN_LEO]: LeoShape,
  [ZodiacSign.ZODIAC_SIGN_VIRGO]: VirgoShape,
  [ZodiacSign.ZODIAC_SIGN_LIBRA]: LibraShape,
  [ZodiacSign.ZODIAC_SIGN_SCORPIO]: ScorpioShape,
  [ZodiacSign.ZODIAC_SIGN_SAGITTARIUS]: SagittariusShape,
  [ZodiacSign.ZODIAC_SIGN_CAPRICORN]: CapricornShape,
  [ZodiacSign.ZODIAC_SIGN_AQUARIUS]: AquariusShape,
  [ZodiacSign.ZODIAC_SIGN_PISCES]: PiscesShape
};

interface EditZodiacPopupProps {
  handleClose: () => void;
}

const EditZodiacPopup: FC<EditZodiacPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);

  const { aboutMe, updateProfile } = useProfileStore();
  const { t } = useTranslation();

  const zodiacSigns = getZodiacSigns(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [selectedSign, setSelectedSign] = useState<ZodiacSign>(
    aboutMe?.profile.zodiac_sign ?? ZodiacSign.ZODIAC_SIGN_UNSPECIFIED
  );

  const selectZodiacSign = (value: ZodiacSign) => {
    setIsEdited(true);
    setSelectedSign(value);
  };

  const submitZodiacSign = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ zodiac_sign: selectedSign });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitZodiacSign);
    return () => mainButton.offClick(submitZodiacSign);
  }, [selectedSign]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <ZodiacShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_zodiac')}</div>
      </div>
      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(zodiacSigns).map(([value, label]) => {
                const ZodiacIcon = zodiacIcons[value as ZodiacSign];
                return (
                  <div
                    key={value}
                    onClick={() => selectZodiacSign(value as ZodiacSign)}
                    style={{
                      ...listItemStyle,
                      ...(selectedSign === (value as ZodiacSign)
                        ? selectedItemStyle
                        : unselectedItemStyle),
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      {ZodiacIcon &&
                        (value as ZodiacSign) !==
                          ZodiacSign.ZODIAC_SIGN_UNSPECIFIED && (
                          <ZodiacIcon width={20} height={20} fillColor="#000" />
                        )}
                      {label}
                    </div>
                    {selectedSign === (value as ZodiacSign) && (
                      <span style={selectedItemMarkStyle}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditZodiacPopup;
